/**
 * useGuideFeatures — handles widget/feature completion and cancellation for CoPilotGuide.
 */

import type { Chat, CoPilotGuideEngine } from '@tonnie/copilot';
import { useCallback } from 'react';

import type { IMessage } from '../types';
import type {
  GuideAnalyticsEventType,
  GuideTraceEventType,
  GuideTraceEvent,
} from '../copilot-guide.types';

interface UseGuideFeaturesArgs {
  engine: CoPilotGuideEngine;
  flowId: string | undefined;
  /**
   * Journey scope. Required to key the localStorage session entry —
   * without it, switching journeys via the global selector hijacks the
   * new journey's flow with the previous journey's cached chat.
   */
  journeyId: string | null | undefined;
  currentFeature: {
    chatId: string;
    feature: string;
    config: Record<string, unknown>;
    displayMode: 'blended' | 'modal';
  } | null;
  safeTimeout: (fn: () => void, ms: number) => ReturnType<typeof setTimeout>;
  setChatMessages: React.Dispatch<React.SetStateAction<IMessage[]>>;
  setActions: React.Dispatch<React.SetStateAction<import('@tonnie/copilot').Action[]>>;
  setCurrentChat: React.Dispatch<React.SetStateAction<Chat | null>>;
  setCurrentFeature: React.Dispatch<
    React.SetStateAction<{
      chatId: string;
      feature: string;
      config: Record<string, unknown>;
      displayMode: 'blended' | 'modal';
    } | null>
  >;
  setAnnouncement: React.Dispatch<React.SetStateAction<string>>;
  emitEvent: (type: GuideAnalyticsEventType, meta?: Record<string, unknown>) => void;
  emitTrace: (
    type: GuideTraceEventType,
    extra?: Partial<Omit<GuideTraceEvent, 'id' | 'type' | 'timestamp'>>
  ) => void;
  updateProgress: () => void;
  processChat: (chat: Chat) => void;
  onComplete?: (payload: { redirect?: string; [key: string]: unknown }) => void;
  markFlowCompleted: () => void;
}

export function useGuideFeatures({
  engine,
  flowId,
  journeyId,
  currentFeature,
  safeTimeout,
  setChatMessages,
  setActions,
  setCurrentChat,
  setCurrentFeature,
  setAnnouncement,
  emitEvent,
  emitTrace,
  updateProgress,
  processChat,
  onComplete,
  markFlowCompleted,
}: UseGuideFeaturesArgs) {
  const handleFeatureComplete = useCallback(
    (data?: unknown) => {
      if (!currentFeature) return;
      const chatId = currentFeature.chatId;
      const chat = engine.getChat(chatId);
      const isSyntheticEvent =
        data &&
        typeof data === 'object' &&
        ('_reactName' in (data as object) || 'nativeEvent' in (data as object));

      if (data && typeof data === 'object' && !Array.isArray(data) && !isSyntheticEvent) {
        Object.entries(data as Record<string, unknown>).forEach(([k, v]) => {
          engine.setVariable(k, v);
        });
      } else if (Array.isArray(data)) {
        const featureName = currentFeature.feature;
        const chat2 = engine.getChat(chatId);
        const stateKey = chat2?.metadata?.feature_state ?? `${featureName}_result`;
        engine.setVariable('purposes', data);
        engine.setVariable(stateKey, data);
      }

      emitEvent('widget_completed', { chatId, widgetName: currentFeature.feature });
      emitTrace('widget_completed', {
        chatId,
        widgetName: currentFeature.feature,
        data: isSyntheticEvent ? undefined : data,
      });
      emitTrace('variables_snapshot', { data: engine.getState().variables });
      setCurrentFeature(null);

      const completionMsg = chat?.metadata?.completionMessage;
      if (completionMsg) {
        const text = engine.replaceVariables(completionMsg);
        setChatMessages((prev) => {
          if (prev.some((m) => m.id.startsWith(`completion_${chatId}`))) return prev;
          return [
            ...prev,
            {
              id: `completion_${chatId}_${Date.now()}`,
              type: 'bot' as const,
              content: text,
              timestamp: new Date(),
              isTyping: true,
              isCompletionMessage: true,
            },
          ];
        });
        safeTimeout(
          () => {
            setChatMessages((prev) =>
              prev.map((m) =>
                m.id.startsWith(`completion_${chatId}`) ? { ...m, isTyping: false } : m
              )
            );
            setAnnouncement(text);
          },
          (text.length / 30) * 1000
        );
      }

      // Rebuild intercept
      const widgetAction =
        data && typeof data === 'object' && !Array.isArray(data)
          ? (data as Record<string, unknown>).action
          : undefined;
      if (widgetAction === 'rebuild') {
        const dataObj = data as Record<string, unknown>;
        const rebuildTarget =
          (dataObj.rebuildTarget as string | undefined) ??
          (chat?.metadata?.rebuildTarget as string | undefined);
        if (rebuildTarget) {
          setChatMessages([]);
          setActions([]);
          const rebuildResult = engine.transition(rebuildTarget);
          if (rebuildResult.nextChatId) {
            const next = engine.getCurrentChat();
            if (next) {
              setCurrentChat(next);
              processChat(next);
              safeTimeout(updateProgress, 100);
            }
          }
        }
        return;
      }

      const result = engine.handleFeatureComplete(chatId, data);
      if (result.nextChatId) {
        const delay = completionMsg ? Math.max((completionMsg.length / 30) * 1000, 800) + 200 : 200;
        safeTimeout(() => {
          const next = engine.getCurrentChat();
          if (next) {
            setCurrentChat(next);
            processChat(next);
            safeTimeout(updateProgress, 100);
          }
        }, delay);
      } else if (result.shouldClose) {
        markFlowCompleted();
        import('../guide-session').then(({ clearSessionState }) =>
          clearSessionState(flowId, journeyId ?? null)
        );
        // No `saveCompletedState()` here — see `use-co-pilot-guide.ts`
        // sentinel handlers for the rationale. Host's `onComplete`
        // (via `useGuideHostFinalize`) is the canonical writer; the
        // duplicate save races finalize on the same rowVersion.
        emitEvent('flow_completed');
        onComplete?.({ redirect: result.shouldRedirect, ...engine.getAllVariables() });
      }
    },
    [
      currentFeature,
      engine,
      processChat,
      updateProgress,
      onComplete,
      safeTimeout,
      emitEvent,
      emitTrace,
      flowId,
      markFlowCompleted,
      setChatMessages,
      setActions,
      setCurrentChat,
      setCurrentFeature,
      setAnnouncement,
    ]
  );

  const handleFeatureCancel = useCallback(() => {
    if (!currentFeature) return;
    const chatId = currentFeature.chatId;
    const chat = engine.getChat(chatId);
    emitEvent('widget_cancelled', { chatId, widgetName: currentFeature.feature });
    emitTrace('widget_cancelled', { chatId, widgetName: currentFeature.feature });
    setCurrentFeature(null);
    const cancelMsg = chat?.metadata?.cancelMessage;
    if (cancelMsg) {
      const text = engine.replaceVariables(cancelMsg);
      setChatMessages((prev) => [
        ...prev,
        {
          id: `cancel_${chatId}_${Date.now()}`,
          type: 'bot' as const,
          content: text,
          timestamp: new Date(),
          isTyping: true,
        },
      ]);
      safeTimeout(
        () => {
          setChatMessages((prev) =>
            prev.map((m) => (m.id.startsWith(`cancel_${chatId}`) ? { ...m, isTyping: false } : m))
          );
        },
        (text.length / 30) * 1000
      );
    }
    const result = engine.handleFeatureCancel(chatId);
    if (result.nextChatId) {
      const delay = cancelMsg ? Math.max((cancelMsg.length / 30) * 1000, 800) + 200 : 200;
      safeTimeout(() => {
        const next = engine.getCurrentChat();
        if (next) {
          setCurrentChat(next);
          processChat(next);
          safeTimeout(updateProgress, 100);
        }
      }, delay);
    }
  }, [
    currentFeature,
    engine,
    processChat,
    updateProgress,
    safeTimeout,
    emitEvent,
    emitTrace,
    setChatMessages,
    setCurrentChat,
    setCurrentFeature,
  ]);

  return { handleFeatureComplete, handleFeatureCancel };
}
